import { useState, useEffect } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
  useMediaQuery,
  Theme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { ChromePicker } from 'react-color'
import { Colorize } from '@mui/icons-material'

interface ColorPickerDialogProps {
  open: boolean
  onClose: () => void
  onColorSelect: (colors: string[]) => void
  selectedColors: string[]
}

const removeCustomIdentifier = (color: string | undefined | null) => {
  if (!color) return '#FFF'
  return color.includes('-custom') ? color.replace('-custom', '') : color
}

const ColorPickerDialog = ({
  open,
  onClose,
  onColorSelect,
  selectedColors,
}: ColorPickerDialogProps) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
  const [color, setColor] = useState(removeCustomIdentifier(selectedColors[0]) || '#fff')
  const [localColors, setLocalColors] = useState<string[]>(
    selectedColors.map(removeCustomIdentifier),
  )

  useEffect(() => {
    setColor(removeCustomIdentifier(selectedColors[0]) || '#FFF')
    setLocalColors(selectedColors.map(removeCustomIdentifier))
  }, [selectedColors, open])

  const handleColorChange = (newColor: any) => {
    setColor(newColor.hex)

    const rgb = hexToRgb(newColor.hex)
    if (!rgb) return

    const colors: { r: number; g: number; b: number }[] = [rgb]
    for (let i = 1; i < 4; i++) {
      const previousColor = colors[i - 1]
      const newR = Math.min(255, previousColor.r + 35)
      const newG = Math.min(255, previousColor.g + 25)
      const newB = previousColor.b
      colors.push({ r: newR, g: newG, b: newB })
    }
    // const updatedColors = [...selectedColors];
    // updatedColors[0] = newColor.hex;
    const updatedColors = colors.map(rgbToHex)
    setLocalColors(updatedColors)
  }

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null
  }

  const rgbToHex = (rgb: { r: number; g: number; b: number }) => {
    return (
      '#' +
      [rgb.r, rgb.g, rgb.b]
        .map((x) => {
          const hex = x.toString(16)
          return hex.length === 1 ? '0' + hex : hex
        })
        .join('')
        .toUpperCase()
    )
  }

  const handleSelectClick = () => {
    onColorSelect(localColors)
    onClose()
  }

  const onClickEyeDropper = async () => {
    if ('EyeDropper' in window) {
      const eyeDropper = new (window as any).EyeDropper()
      try {
        const result = await eyeDropper.open()
        handleColorChange({
          hex: result.sRGBHex,
        })
      } catch (error) {
        console.error('EyeDropper canceled or failed', error)
      }
    } else {
      alert('EyeDropper API is not supported in this browser.')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      BackdropProps={{
        style: { backgroundColor: 'transparent' },
      }}
      sx={{
        '& .MuiPaper-root': {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.7)',
          borderRadius: '8px',
        },
        '& .MuiDialog-paper': {
          position: isSmallScreen ? 'relative' : 'absolute',
          top: isSmallScreen ? null : '120px',
          left: isSmallScreen ? null : '300px',
        },
      }}
    >
      <DialogTitle>Select a Color</DialogTitle>
      <IconButton
        aria-label='close'
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <ChromePicker
          color={color}
          onChangeComplete={handleColorChange}
          disableAlpha={true}
          styles={{
            default: {
              picker: {
                width: '300px',
              },
            },
          }}
        />
        <Box display='flex' justifyContent='center' mt={2} alignItems={'center'}>
          {Array(4)
            .fill(null)
            .map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: localColors[index] || '#e0e0e0', // Use the selected color or default gray
                  margin: '0 8px',
                  border: '1px solid #ccc',
                }}
              />
            ))}

          <IconButton onClick={onClickEyeDropper} sx={{ marginLeft: '12px' }}>
            <Colorize sx={{ height: '32px', width: '32px' }} />
          </IconButton>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSelectClick} color='primary'>
          Select
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ColorPickerDialog
